/**
 * Pooled render objects for the twelve attack patterns.
 *
 * RESPONSIBILITY SPLIT — *core decides, Phaser renders*. The deterministic sim
 * (`core/world.ts`) owns every hazard's geometry and its telegraph/active
 * windows; this pool owns only the *visuals*. It hands the {@link PatternDirector}
 * a recycled {@link Phaser.GameObjects.Image} per hazard kind, which the director
 * positions/rotates/scales/tints to match a {@link Hazard} descriptor each frame.
 *
 * Like {@link import('./ObstaclePool').ObstaclePool}, instances are reused, never
 * allocated/destroyed per spawn, to avoid GC stutter during a run. Unlike the
 * obstacle pool these objects carry NO arcade body — collision is computed in
 * core (`hazardHitsPlayer`), so a hazard sprite is purely decorative.
 *
 * One pool bucket per {@link HazardKind}; each bucket draws from a single hazard
 * texture (see src/game/textures.ts) and is tinted/stretched at render time:
 *  - `rect`            → the wall bar texture, stretched to `w`×`h`.
 *  - `segment`         → the beam strip, rotated to the segment angle + stretched
 *                        to its length × `thickness`.
 *  - `rotatingSegment` → one beam sprite PER beam (the caro draws `beams` strips
 *                        sharing a centre); the director leases that many.
 *  - `circleMover`     → the missile body (missiles family) or bomb body
 *                        (bombs family), scaled to `2r`.
 */
import Phaser from 'phaser';
import type { Hazard } from '../core';
import { HAZARD_TEX } from './textures';

/** Render depth band for hazards — above the play field, below the player (10). */
const HAZARD_DEPTH = 5;

/** A leased sprite, tagged with the texture key it currently shows. */
type Sprite = Phaser.GameObjects.Image;

/**
 * A simple free-list of {@link Phaser.GameObjects.Image} sharing one texture.
 * `acquire()` reuses a hidden sprite or makes one; `release()` hides it for reuse.
 */
class SpriteBucket {
  private readonly scene: Phaser.Scene;
  private readonly textureKey: string;
  private readonly all: Sprite[] = [];
  private readonly free: Sprite[] = [];

  constructor(scene: Phaser.Scene, textureKey: string) {
    this.scene = scene;
    this.textureKey = textureKey;
  }

  /** Take a visible sprite from the pool (creating one only if none are free). */
  acquire(): Sprite {
    const sprite = this.free.pop() ?? this.create();
    sprite.setActive(true).setVisible(true);
    return sprite;
  }

  /** Return a sprite to the pool — hidden, reset, ready to lease again. */
  release(sprite: Sprite): void {
    sprite
      .setActive(false)
      .setVisible(false)
      .setAngle(0)
      .setScale(1)
      .setAlpha(1)
      .clearTint()
      .setOrigin(0.5, 0.5);
    this.free.push(sprite);
  }

  private create(): Sprite {
    const sprite = this.scene.add.image(0, 0, this.textureKey).setDepth(HAZARD_DEPTH);
    this.all.push(sprite);
    return sprite;
  }

  /** Destroy every sprite this bucket has ever created (scene teardown). */
  destroyAll(): void {
    for (const sprite of this.all) sprite.destroy();
    this.all.length = 0;
    this.free.length = 0;
  }
}

/**
 * The set of render buckets, keyed by hazard texture. {@link PatternDirector}
 * leases the right number of sprites for a hazard, then releases them when the
 * hazard expires and is dropped from `world.hazards`.
 */
export class HazardPool {
  private readonly wall: SpriteBucket;
  private readonly beam: SpriteBucket;
  private readonly missile: SpriteBucket;
  private readonly bomb: SpriteBucket;
  private readonly turret: SpriteBucket;

  constructor(scene: Phaser.Scene) {
    this.wall = new SpriteBucket(scene, HAZARD_TEX.wall);
    this.beam = new SpriteBucket(scene, HAZARD_TEX.beam);
    this.missile = new SpriteBucket(scene, HAZARD_TEX.missile);
    this.bomb = new SpriteBucket(scene, HAZARD_TEX.bomb);
    this.turret = new SpriteBucket(scene, HAZARD_TEX.turret);
  }

  /**
   * How many sprites a hazard needs:
   *  - `rotatingSegment` → one per beam (the caro),
   *  - everything else   → one.
   */
  spriteCountFor(hazard: Hazard): number {
    return hazard.kind === 'rotatingSegment' ? hazard.beams : 1;
  }

  /**
   * Lease `count` sprites of the visual appropriate for `hazard`. The director
   * calls this once when it first sees a hazard id and keeps the returned array
   * for the hazard's lifetime. Beam-family rotating segments get `beams` beam
   * sprites; rects get a wall; circle movers get a missile or bomb body by family.
   */
  acquireFor(hazard: Hazard, count: number): Sprite[] {
    const bucket = this.bucketFor(hazard);
    const sprites: Sprite[] = [];
    for (let i = 0; i < count; i++) sprites.push(bucket.acquire());
    return sprites;
  }

  /** Return a hazard's leased sprites to their pool (the hazard has expired). */
  releaseFor(hazard: Hazard, sprites: Sprite[]): void {
    const bucket = this.bucketFor(hazard);
    for (const sprite of sprites) bucket.release(sprite);
  }

  /** Destroy all sprites — used on scene shutdown. */
  destroy(): void {
    this.wall.destroyAll();
    this.beam.destroyAll();
    this.missile.destroyAll();
    this.bomb.destroyAll();
    this.turret.destroyAll();
  }

  /** Pick the bucket whose texture matches the hazard's kind/family. */
  private bucketFor(hazard: Hazard): SpriteBucket {
    switch (hazard.kind) {
      case 'rect':
        return this.wall;
      case 'segment':
      case 'rotatingSegment':
        return this.beam;
      case 'circleMover':
        // Bomb vs missile share the circle-mover kind; the family picks the art.
        return hazard.family === 'bombs' ? this.bomb : this.missile;
    }
  }
}
